import * as React from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { paymentSchema, PASSWORD_RULES, type PaymentForm } from "../schemas/paymentSchema";
import type { BuildPlanResponse, PaymentResult, AccountResult, PlanLineItem, WizardStep } from "../wizardTypes";
import { companyFromEmail, fmtMoney } from "../data/planMatch";
import { fmtRate } from "../data/rackRates";
import { createAccount, SignupApiError } from "../api";
import styles from "../css/SignupWizard.module.css";

/** Valid product keys for payment plan lines. */
const VALID_PRODUCT_KEYS = new Set<PlanLineItem["productKey"]>(["dncscrub", "reassigned", "trustcall", "complianceguide"]);

type Props = {
  plan: BuildPlanResponse;
  email: string;
  onSubmit: (payment: PaymentResult, account: AccountResult) => void;
  onGoToStep?: (step: WizardStep) => void;
  onPaymentStarted?: () => void;
  onPaymentFailed?: (reasonCode: string) => void;
};

// Format raw phone digits to (XXX) XXX-XXXX
function formatPhoneValue(val: string): string {
  const cleaned = val.replace(/\D/g, "").substring(0, 10);
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : cleaned;
}

export function PaymentStep({ plan, email, onSubmit, onGoToStep, onPaymentStarted, onPaymentFailed }: Props) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Suggest the email local part (jeffwhuber@dnc.com → jeffwhuber), which is
  // how most DNCScrub usernames look. A "+tag" is tester noise, drop it.
  const defaultUsername = email.split("@")[0].replace(/\+.*$/, "");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PaymentForm>({
    resolver: zodResolver(paymentSchema),
    mode: "onBlur",
    defaultValues: {
      email,
      company: companyFromEmail(email),
      cardholderName: "",
      cardNumber: "",
      expirationMonth: "",
      expirationYear: "",
      cvv: "",
      username: defaultUsername,
      phoneNumber: "",
    },
  });

  const password = watch("password") ?? "";
  const confirmPassword = watch("confirmPassword") ?? "";

  const ruleChecks = [
    ...PASSWORD_RULES.map((r) => ({
      label: r.label,
      pass: r.test(password),
    })),
    {
      label: "Both passwords match",
      pass: password.length > 0 && password === confirmPassword,
    },
  ];

  // Format card number as "4242 4242 4242 4242" while typing
  const handleCardNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, "").slice(0, 19);
      const formatted = digits.replace(/(\d{4})(?=\d)/g, "$1 ");
      e.target.value = formatted;
      setValue("cardNumber", formatted, { shouldValidate: false });
    },
    [setValue],
  );

  // Format phone number as (XXX) XXX-XXXX while typing
  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneValue(e.target.value);
      e.target.value = formatted;
      setValue("phoneNumber", formatted, { shouldValidate: false });
    },
    [setValue],
  );

  const handleFormSubmit = async (data: PaymentForm) => {
    // Count valid submissions even if Turnstile blocks them. Analytics failures
    // must not prevent the purchase.
    try {
      onPaymentStarted?.();
    } catch (err) {
      console.warn("payment_started tracking failed", err);
    }

    if (!turnstileToken) {
      setApiError("Please complete the security verification.");
      return;
    }

    setApiError(null);
    setSubmitting(true);

    const planLines: PlanLineItem[] = plan.lines
      .filter((l) => VALID_PRODUCT_KEYS.has(l.productKey as PlanLineItem["productKey"]))
      .map((l) => ({
        productKey: l.productKey as PlanLineItem["productKey"],
        // The server resolves the SelfServePlans row by (productKey, includedQty); only the
        // flat-priced Compliance Guide has no tier.
        tierVolume: l.productKey === "complianceguide" ? null : l.includedQty,
        computedPrice: l.price,
        period: l.period,
      }));

    try {
      const result = await createAccount({
        plan: planLines,
        billing: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          company: data.company,
          phone: data.phoneNumber,
          address: {
            street: data.street,
            city: data.city,
            state: data.state,
            zip: data.zip,
          },
        },
        card: {
          cardNumber: data.cardNumber,
          expirationMonth: data.expirationMonth,
          expirationYear: data.expirationYear,
          cvv: data.cvv,
          cardholderName: data.cardholderName,
        },
        turnstileToken,
        username: data.username,
        password: data.password,
      });

      const paymentResult: PaymentResult = {
        accountId: result.accountId,
        lastFour: result.lastFour,
        chargedAmount: result.chargedAmount,
        nextChargeDate: result.nextChargeDate,
      };
      const accountResult: AccountResult = {
        username: result.username,
        loginUrl: result.loginUrl,
        isTestSignup: result.isTestSignup ?? false,
      };

      onSubmit(paymentResult, accountResult);
    } catch (err) {
      turnstileRef.current?.reset();
      setTurnstileToken(null);

      if (err instanceof SignupApiError) {
        setApiError(err.message);
        onPaymentFailed?.(err.code);
      } else {
        setApiError("Something went wrong. Please try again.");
        onPaymentFailed?.("unknown");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const monthlyLines = plan.lines.filter((l) => l.period === "mo");
  const annualLines = plan.lines.filter((l) => l.period === "yr");
  const linesWithOverage = plan.lines.filter((l) => l.overageRate != null);

  return (
    <div className={styles.payWrap}>
      <p className={styles.planProgress}>
        <span className={styles.planProgressStep}>Step 3 of 3</span> &middot;
        Payment
      </p>
      <h2
        className={styles.pageTitle}
        style={{ margin: "0.3rem 0 1.25rem", fontSize: 26 }}
      >
        Last step &mdash; then upload your first list.
      </h2>

      {apiError && <div className={styles.errorBanner}>{apiError}</div>}

      <div className={styles.payGrid}>
        {/* Left column: form */}
        <div>
          <form id="payment-form" onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Your account */}
            <h3 className={styles.paySection}>Your account</h3>
            <div className={styles.payFields}>
              <label className={styles.payFieldHalf}>
                <span>First name</span>
                <input
                  className={`${styles.payInput} ${errors.firstName ? styles.inputError : ""}`}
                  autoComplete="given-name"
                  {...register("firstName")}
                />
                {errors.firstName && <p className={styles.errorText}>{errors.firstName.message}</p>}
              </label>
              <label className={styles.payFieldHalf}>
                <span>Last name</span>
                <input
                  className={`${styles.payInput} ${errors.lastName ? styles.inputError : ""}`}
                  autoComplete="family-name"
                  {...register("lastName")}
                />
                {errors.lastName && <p className={styles.errorText}>{errors.lastName.message}</p>}
              </label>
              <label className={styles.payField}>
                <span>Work email</span>
                <input
                  type="email"
                  className={styles.payInput}
                  autoComplete="email"
                  readOnly
                  {...register("email")}
                />
                <em className={styles.payCarried}>
                  From step 1 &mdash; this becomes your login
                </em>
              </label>
              <label className={styles.payFieldHalf}>
                <span>Company</span>
                <input
                  className={`${styles.payInput} ${errors.company ? styles.inputError : ""}`}
                  autoComplete="organization"
                  {...register("company")}
                />
                {errors.company && <p className={styles.errorText}>{errors.company.message}</p>}
              </label>
              <label className={styles.payFieldHalf}>
                <span>Phone number</span>
                <input
                  type="tel"
                  className={`${styles.payInput} ${errors.phoneNumber ? styles.inputError : ""}`}
                  autoComplete="tel"
                  inputMode="tel"
                  {...register("phoneNumber", { onChange: handlePhoneChange })}
                />
                {errors.phoneNumber && <p className={styles.errorText}>{errors.phoneNumber.message}</p>}
              </label>
            </div>

            {/* Login credentials */}
            <h3 className={styles.paySection}>Create your login</h3>
            <div className={styles.payFields}>
              <label className={styles.payField}>
                <span>Username</span>
                <input
                  type="text"
                  className={`${styles.payInput} ${errors.username ? styles.inputError : ""}`}
                  autoComplete="username"
                  {...register("username")}
                />
                <em className={styles.payCarried}>
                  Suggested from your email &mdash; change it if you like
                </em>
              </label>
              <label className={styles.payField}>
                <span>Password</span>
                <input
                  type="password"
                  className={`${styles.payInput} ${errors.password ? styles.inputError : ""}`}
                  autoComplete="new-password"
                  {...register("password")}
                />
              </label>
              <label className={styles.payField}>
                <span>Confirm password</span>
                <input
                  type="password"
                  className={`${styles.payInput} ${errors.confirmPassword ? styles.inputError : ""}`}
                  autoComplete="new-password"
                  {...register("confirmPassword")}
                />
              </label>
            </div>

            {/* Password rules */}
            <ul className={styles.loginRules}>
              {ruleChecks.map((check) => (
                <li
                  key={check.label}
                  className={check.pass ? styles.loginRulePass : undefined}
                >
                  {check.label}
                </li>
              ))}
            </ul>

            {/* Billing address */}
            <h3 className={styles.paySection} style={{ marginTop: "1.5rem" }}>Billing address</h3>
            <p className={styles.paySectionSub}>
              Needed to calculate sales tax and to match your card.
            </p>
            <div className={styles.payFields}>
              <label className={styles.payField}>
                <span>Street</span>
                <input
                  className={`${styles.payInput} ${errors.street ? styles.inputError : ""}`}
                  autoComplete="street-address"
                  {...register("street")}
                />
                {errors.street && <p className={styles.errorText}>{errors.street.message}</p>}
              </label>
              <label className={styles.payFieldHalf}>
                <span>City</span>
                <input
                  className={`${styles.payInput} ${errors.city ? styles.inputError : ""}`}
                  autoComplete="address-level2"
                  {...register("city")}
                />
                {errors.city && <p className={styles.errorText}>{errors.city.message}</p>}
              </label>
              <label className={styles.payFieldQuarter}>
                <span>State</span>
                <input
                  className={`${styles.payInput} ${errors.state ? styles.inputError : ""}`}
                  autoComplete="address-level1"
                  placeholder="CA"
                  maxLength={2}
                  {...register("state")}
                />
                {errors.state && <p className={styles.errorText}>{errors.state.message}</p>}
              </label>
              <label className={styles.payFieldQuarter}>
                <span>ZIP</span>
                <input
                  className={`${styles.payInput} ${errors.zip ? styles.inputError : ""}`}
                  autoComplete="postal-code"
                  placeholder="95404"
                  {...register("zip")}
                />
                {errors.zip && <p className={styles.errorText}>{errors.zip.message}</p>}
              </label>
            </div>

            {/* Card */}
            <h3 className={styles.paySection}>Card</h3>
            <div className={styles.payFieldsCard}>
              <label className={styles.payField}>
                <span>Cardholder name</span>
                <input
                  className={`${styles.payInput} ${errors.cardholderName ? styles.inputError : ""}`}
                  autoComplete="cc-name"
                  {...register("cardholderName")}
                />
                {errors.cardholderName && <p className={styles.errorText}>{errors.cardholderName.message}</p>}
              </label>
              <label className={styles.payField}>
                <span>Card number</span>
                <input
                  className={`${styles.payInput} ${errors.cardNumber ? styles.inputError : ""}`}
                  autoComplete="cc-number"
                  inputMode="numeric"
                  placeholder="1234 1234 1234 1234"
                  {...register("cardNumber", { onChange: handleCardNumberChange })}
                />
                {errors.cardNumber && <p className={styles.errorText}>{errors.cardNumber.message}</p>}
              </label>
              <label className={styles.payFieldQuarter}>
                <span>Month</span>
                <input
                  className={`${styles.payInput} ${errors.expirationMonth ? styles.inputError : ""}`}
                  autoComplete="cc-exp-month"
                  inputMode="numeric"
                  placeholder="MM"
                  maxLength={2}
                  {...register("expirationMonth")}
                />
                {errors.expirationMonth && <p className={styles.errorText}>{errors.expirationMonth.message}</p>}
              </label>
              <label className={styles.payFieldQuarter}>
                <span>Year</span>
                <input
                  className={`${styles.payInput} ${errors.expirationYear ? styles.inputError : ""}`}
                  autoComplete="cc-exp-year"
                  inputMode="numeric"
                  placeholder="YYYY"
                  maxLength={4}
                  {...register("expirationYear")}
                />
                {errors.expirationYear && <p className={styles.errorText}>{errors.expirationYear.message}</p>}
              </label>
              <label className={styles.payFieldHalf}>
                <span>CVV</span>
                <input
                  className={`${styles.payInput} ${errors.cvv ? styles.inputError : ""}`}
                  autoComplete="cc-csc"
                  inputMode="numeric"
                  placeholder="CVV"
                  maxLength={4}
                  {...register("cvv")}
                />
                {errors.cvv && <p className={styles.errorText}>{errors.cvv.message}</p>}
              </label>
            </div>

            {/* Turnstile bot protection */}
            <div style={{ marginTop: "1.5rem" }}>
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_CLOUDFLARE_SITEKEY}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                options={{ theme: "light", size: "normal" }}
              />
            </div>
          </form>
        </div>

        {/* Right column: summary sidebar */}
        <aside className={styles.paySide}>
          <div className={styles.paySummary}>
            <p className={styles.paySummaryLabel}>Your plan</p>

            {/* Line items */}
            <ul className={styles.paySummaryLines}>
              {monthlyLines.map((l) => (
                <li key={l.productKey}>
                  <div>
                    <span>{l.name}</span>
                    <em>{l.coverage}</em>
                  </div>
                  <strong>{fmtMoney(l.price)}/mo</strong>
                </li>
              ))}
              {annualLines.map((l) => (
                <li key={l.productKey}>
                  <span>{l.name}</span>
                  <strong>{fmtMoney(l.price)}/yr</strong>
                </li>
              ))}
            </ul>

            {/* Totals */}
            <div className={styles.payTotals}>
              {plan.monthlyTotal > 0 && (
                <p className={styles.payRow}>
                  <span>Monthly</span>
                  <span>{fmtMoney(plan.monthlyTotal)}/mo</span>
                </p>
              )}
              <p className={styles.payRowMuted}>
                <span>Sales tax</span>
                <span>Calculated at checkout</span>
              </p>
              <p className={styles.payRowTotal}>
                <span>Due today</span>
                <span>{fmtMoney(plan.monthlyTotal + plan.annualTotal)}</span>
              </p>
            </div>

            {/* Authorization */}
            <div className={styles.payAuthorise}>
              You&rsquo;re authorising{" "}
              <strong>{fmtMoney(plan.monthlyTotal + plan.annualTotal)}</strong> today
              {plan.monthlyTotal > 0 && (<>, then{" "}
              <strong>{fmtMoney(plan.monthlyTotal)}</strong> on the same day
              each month until you cancel</>)}
              {plan.annualTotal > 0 && (<>. Annual charges renew on the same date each year</>)}.
            </div>

            {/* Overage info */}
            {linesWithOverage.map((l) => (
              <p key={`ovg-${l.productKey}`} className={styles.payOverage}>
                Above your plan: {l.name} &mdash; {fmtRate(l.overageRate!)} {l.overageUnit}
              </p>
            ))}

            {/* CTA */}
            <button
              type="submit"
              form="payment-form"
              className={styles.payCta}
              disabled={submitting || !turnstileToken}
            >
              {submitting ? "Processing\u2026" : "Pay and create account"}
            </button>

            {/* Terms */}
            <p className={styles.payTerms}>
              Services Agreement accepted at step 2 &middot;{" "}
              <a
                href="https://www.dnc.com/services-agreement"
                target="_blank"
                rel="noopener noreferrer"
              >
                review
              </a>
              <br />
              Monthly term &middot; renews automatically &middot; 30
              days&rsquo; notice to cancel
            </p>

            {/* Back */}
            {onGoToStep && (
              <p className={styles.payBack}>
                <button
                  type="button"
                  onClick={() => onGoToStep("plan")}
                >
                  &larr; Back to your plan
                </button>
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
